import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/common/Button';
import apiClient from '../services/apiClient';
import ParticleBackground from '../components/ParticleBackground';

interface CalendarCallbackResponse {
  success: boolean;
  message?: string;
}

const CalendarCallbackPage: React.FC = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState<string>('Processing your request...');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const processCallback = async () => {
      try {
        // Get the code and state from URL query parameters
        const params = new URLSearchParams(location.search);
        const code = params.get('code');
        const state = params.get('state');

        if (!code) {
          setStatus('error');
          setMessage('No authorization code received from Google');
          return;
        }

        // Send the code to our backend
        const response = await apiClient.get<CalendarCallbackResponse>(
          `/calendar/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state || '')}`
        );

        if (response?.success) {
          setStatus('success');
          setMessage('Google Calendar connected successfully!');
        } else {
          setStatus('error');
          setMessage(response?.message || 'Failed to connect Google Calendar');
        }
      } catch (error) {
        console.error('Error processing calendar callback:', error);
        setStatus('error');
        setMessage('An error occurred while connecting to Google Calendar');
      }
    };

    processCallback();
  }, [location.search]);

  const handleClose = () => {
    navigate('/settings-preferences');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-light dark:bg-background-dark">
      <ParticleBackground />
      <div className="text-center bg-background-light/50 dark:bg-background-dark/50 p-6 rounded-xl border border-border-light dark:border-border-dark backdrop-blur-sm">
        {status === 'loading' && (
          <div className="text-center justify-center">
            <Loader2 className="w-12 h-12 mx-auto mb-4 text-primary-600 dark:text-primary-400 animate-spin" />
            <h2 className="text-xl font-semibold mb-2">Connecting to Google Calendar</h2>
            <p className="text-gray-500 dark:text-gray-400">Please wait while we complete the connection...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center justify-center">
            <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
            <h2 className="text-xl font-semibold mb-2">Success!</h2>
            <p className="text-gray-500 dark:text-gray-400 mb-6">{message}</p>
            <Button variant="primary" className="w-full" onClick={handleClose}>
              Return to Settings
            </Button>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center justify-center items-center">
            <XCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
            <h2 className="text-xl font-semibold mb-2">Connection Failed</h2>
            <p className="text-gray-500 dark:text-gray-400 mb-6 text-center">{message}</p>
            <Button variant="primary" className="w-full" onClick={handleClose}>
              Return to Settings
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarCallbackPage; 